// Shared by pages and worker. IndexedDB selects the current immutable CacheStorage entry;
// an old tab can finish writing its own entry without replacing a newer tab's authority.
import Dexie from 'dexie'

export const PRIVATE_AUTHORITY_CACHE = 'telos-private-authority-v1'
const KEY = '/telos-private-authority'
type AuthorityPointer = { key: string; token: string | null }
const db = new Dexie('telos-private-authority')
db.version(1).stores({ pointers: 'key' })
const pointers = db.table<AuthorityPointer, string>('pointers')
const entryKey = (token: string) => `${KEY}/${token}`

export type PrivateAuthority = { owner: string | null; revision: string | null }

export async function capturePrivateAuthority(): Promise<PrivateAuthority> {
  if (typeof caches === 'undefined') return { owner: null, revision: null }
  try {
    const cache = await caches.open(PRIVATE_AUTHORITY_CACHE)
    for (;;) {
      const current = await pointers.get(KEY)
      if (!current?.token) return { owner: null, revision: null }
      const response = await cache.match(entryKey(current.token))
      const owner = await response?.text() ?? null
      if ((await pointers.get(KEY))?.token === current.token) return { owner, revision: current.token }
    }
  } catch { return { owner: null, revision: null } }
}
export async function privateAuthority(): Promise<string | null> {
  return (await capturePrivateAuthority()).owner
}

export async function setPrivateAuthority(owner: string | null, isCurrent: () => boolean = () => true): Promise<void> {
  await publishPrivateAuthority(owner, isCurrent)
}

/** Compare at the shared pointer transaction, including anonymous→anonymous changes. */
export async function comparePrivateAuthority(expected: PrivateAuthority, owner: string | null): Promise<boolean> {
  return publishPrivateAuthority(owner, () => true, expected.revision)
}

async function publishPrivateAuthority(owner: string | null, isCurrent: () => boolean, expectedRevision?: string | null): Promise<boolean> {
  if (typeof caches === 'undefined') return false
  const token = crypto.randomUUID()
  let previous: string | null = null
  let accepted = false
  try {
    await db.transaction('rw', pointers, async () => {
      // The read acquires the shared transaction lock before checking the caller's
      // browser revision. A stale tab cannot republish after B has committed.
      const current = await pointers.get(KEY)
      if (!isCurrent() || (expectedRevision !== undefined && (current?.token ?? null) !== expectedRevision)) return
      previous = current?.token ?? null
      await pointers.put({ key: KEY, token })
      accepted = true
    })
    if (!accepted || typeof caches === 'undefined') return false
    const cache = await caches.open(PRIVATE_AUTHORITY_CACHE)
    if (owner) await cache.put(entryKey(token), new Response(owner))
    else await cache.delete(KEY) // Remove the old, unversioned CacheStorage authority.
    const current = await pointers.get(KEY)
    if (current?.token !== token) await cache.delete(entryKey(token))
    if (previous && previous !== current?.token) await cache.delete(entryKey(previous))
    return current?.token === token && isCurrent()
  } catch { return false }
}
